import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { useSelector } from 'react-redux'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { clientIntakesLCx } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/constants'
import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { getFriendlyDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { RECORDING_AVAILABLE_STATUSES } from '@lyrahealth-inc/shared-app-logic/src/features/videoSession/constants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import VideoPlayer from '@lyrahealth-inc/ui-core/lib/atoms/videoPlayer/VideoPlayer'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { RadioGroup } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ClientIntakeCancellationPolicyText } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientIntakeCancellationPolicyText/ClientIntakeCancellationPolicyText'
import { FormContentHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/formContentHeader/FormContentHeader'
import { FormBody } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { addLyraSelectClientNoticeToNote } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/notes/utils'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { CareTimelineItem } from './careTimelineUtils'
import { mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import {
  cloudfrontOriginUrl,
  episodeTerminationStatuses,
  episodeTerminationStatusesEngageDA,
  programConfig,
} from '../../common/constants/appConstants'
import { logToSentry } from '../../common/utils/utils'
import { useNavigateToCloseSession } from '../../dashboard/openItems/hooks/useNavigateToCloseSession'
import { getAuthRoles, getAuthUserId } from '../../data/auth/authSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useIsEngageDAProvider } from '../../hooks/useIsEngageDAProvider'
import { useAssignmentsData } from '../assignments/data/hooks/useAssignmentsData'
import { populateInitialValues } from '../clientNotes/ClientNotes'
import { useNoteNavigationActions } from '../clientNotes/hooks/useNoteNavigationActions'
import { ASYNC_TIME_NOTE_NAME, canAuthorAsyncTimeNote } from '../clientNotes/utils/asyncTimeNote'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useGetVideoSessionsQuery, useLazyGetVideoSessionRecordingQuery } from '../data/videoSessionApi'

type UseCareTimelineActionsArgs = {
  appointments: Appointment[]
  episodes: Episode[]
  notes: Note[]
  selectedEpisode?: Episode
  selectEpisode?: (episode: Episode) => void
}

const DischargeHeader = styled.View(({ theme }) => ({
  marginHorizontal: theme.spacing['16px'],
}))

const DischargeFormContainer = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
  padding: theme.spacing['16px'],
}))

const ZENDESK_CARE_REFERRAL_URL =
  'https://provider-support.lyrahealth.com/hc/en-us/restricted?return_to=https%3A%2F%2Fprovider-support.lyrahealth.com%2Fhc%2Fen-us%2Frequests%2Fnew%3Fticket_form_id%3D360000119987'

export const useCareTimelineActions = ({
  appointments,
  episodes,
  notes,
  selectedEpisode,
  selectEpisode,
}: UseCareTimelineActionsArgs) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const userId = useSelector(getAuthUserId)
  const roles = useSelector(getAuthRoles)
  const { isNPTestingEnabled } = useFlags()
  const clientId = useSelector(getSelectedClientId)
  const { data: clientDetails } = useSelectedClientData()
  const { data: assignments = [] } = useAssignmentsData()
  const isDAEngageProvider = useIsEngageDAProvider()
  const [getVideoSessionRecording] = useLazyGetVideoSessionRecordingQuery()
  const { data: videoSessions = [], isLoading: isLoadingVideoSessions } = useGetVideoSessionsQuery(
    clientId ? { providerId: userId, patientId: clientId } : { providerId: userId },
    { skip: !clientId || !userId, refetchOnMountOrArgChange: true },
  )
  const { closeSession, completeNote } = useNavigateToCloseSession({
    originRoute: 'careTimeline',
  })
  const [recordingModalContent, setRecordingModalContent] = useState<JSX.Element | null>(null)
  const videoRef = useRef(null)

  const recordingIdByAppointmentId = useMemo(
    () =>
      Object.fromEntries(
        videoSessions.flatMap((videoSession) => {
          const appointmentId = Number(videoSession.appointment_id)
          return Number.isFinite(appointmentId) && RECORDING_AVAILABLE_STATUSES.has(videoSession.recording_status)
            ? [[appointmentId, videoSession.id]]
            : []
        }),
      ),
    [videoSessions],
  )

  const {
    addSessionNote: addSessionNoteFromItem,
    navigateToExistingNote: navigateToExistingNoteFromItem,
    navigateToNewNote,
    loadContents,
    getEpisode,
    selectItemEpisode,
    showActionError,
  } = useNoteNavigationActions({ appointments, episodes, notes, selectEpisode })

  const addSessionNote = useCallback(
    (item: CareTimelineItem) => {
      if (!item.appointmentId) {
        showActionError()
        return
      }
      return addSessionNoteFromItem({ appointmentId: item.appointmentId, episodeId: item.episodeId })
    },
    [addSessionNoteFromItem, showActionError],
  )

  const navigateToExistingNote = useCallback(
    (item: CareTimelineItem) => {
      if (!item.noteId) {
        return
      }
      return navigateToExistingNoteFromItem({ noteId: item.noteId })
    },
    [navigateToExistingNoteFromItem],
  )

  const addGeneralNote = useCallback(async () => {
    if (!selectedEpisode) {
      showActionError()
      return
    }
    const contents = await loadContents(selectedEpisode)
    if (!contents) {
      return
    }

    const { GENERAL_NOTE, OLD_GENERAL_NOTE } =
      Programs.getProgramConfigByName(selectedEpisode.program_name)?.noteContentMetadataName ?? {}
    const generalNoteMetadata = contents.find(
      (content) => content.name === GENERAL_NOTE || content.name === OLD_GENERAL_NOTE,
    )
    if (!generalNoteMetadata) {
      showActionError()
      return
    }

    const mostRecentCompletedNote = notes
      .filter((note) => note.episode_id === selectedEpisode.id && note.status === NoteStatusStates.COMPLETED)
      .toSorted((firstNote, secondNote) => (secondNote.update_date > firstNote.update_date ? 1 : -1))[0]
    const prepopulationResponses = mostRecentCompletedNote
      ? [
          {
            contentName: mostRecentCompletedNote.content_meta_data.name,
            data: mostRecentCompletedNote.body,
            responseUiSchema: mostRecentCompletedNote.content_meta_data.meta_data.uiSchema,
          },
        ]
      : []

    await navigateToNewNote(
      {
        title: generalNoteMetadata.title,
        content_meta_data: generalNoteMetadata,
        note_type: NoteTypes.GENERAL,
        body: populateInitialValues(prepopulationResponses, GENERAL_NOTE, contents),
      },
      selectedEpisode,
    )
  }, [loadContents, navigateToNewNote, notes, selectedEpisode, showActionError])

  const canAddAsyncTimeNote = canAuthorAsyncTimeNote({ isNPTestingEnabled, episode: selectedEpisode, roles })

  const addAsyncTimeNote = useCallback(async () => {
    if (!selectedEpisode) {
      showActionError()
      return
    }
    const contents = await loadContents(selectedEpisode)
    if (!contents) {
      return
    }

    const asyncTimeNoteMetadata = contents.find((content) => content.name === ASYNC_TIME_NOTE_NAME)
    if (!asyncTimeNoteMetadata) {
      showActionError()
      return
    }

    await navigateToNewNote(
      {
        title: asyncTimeNoteMetadata.title,
        content_meta_data: asyncTimeNoteMetadata,
        note_type: NoteTypes.GENERAL,
      },
      selectedEpisode,
    )
  }, [loadContents, navigateToNewNote, selectedEpisode, showActionError])

  const addCareReferral = useCallback(async () => {
    if (!selectedEpisode) {
      showActionError()
      return
    }
    const contents = await loadContents(selectedEpisode)
    if (!contents) {
      return
    }

    const { REFERRAL_NOTE, OLD_REFERRAL_NOTE } =
      Programs.getProgramConfigByName(selectedEpisode.program_name)?.noteContentMetadataName ?? {}
    let referralNoteMetadata = contents.find(
      (content) => content.name === REFERRAL_NOTE || content.name === OLD_REFERRAL_NOTE,
    )
    if (!referralNoteMetadata) {
      showActionError()
      return
    }

    if (isDAEngageProvider) {
      track({ event: mixpanelEvents.CLICK_REFER_TO_OTHER_CARE })
      if (typeof window !== 'undefined') {
        window.open(ZENDESK_CARE_REFERRAL_URL, '_blank', 'noopener,noreferrer')
      }
      return
    }

    if (clientDetails?.product_type === PRODUCT_TYPE.SELECT) {
      referralNoteMetadata = addLyraSelectClientNoticeToNote(referralNoteMetadata, 'linkToZendesk')
    }
    await navigateToNewNote(
      {
        title: referralNoteMetadata.title,
        content_meta_data: referralNoteMetadata,
        note_type: NoteTypes.REFERRAL,
      },
      selectedEpisode,
    )
  }, [
    clientDetails?.product_type,
    isDAEngageProvider,
    loadContents,
    navigateToNewNote,
    selectedEpisode,
    showActionError,
  ])

  const closeCareTimelineSession = useCallback(
    async (item: CareTimelineItem) => {
      const appointment = appointments.find(({ appointmentId }) => appointmentId === item.appointmentId)
      const episode = getEpisode(item.episodeId)
      if (!appointment || !clientId) {
        showActionError()
        return
      }

      selectItemEpisode(episode)
      track({
        event: mixpanelEvents.CLOSE_SESSION,
        details: {
          appointmentId: appointment.appointmentId,
          entryPoint: 'careTimeline',
        },
      })
      await closeSession(appointment, clientId)
    },
    [appointments, clientId, closeSession, getEpisode, selectItemEpisode, showActionError],
  )

  const completeCareTimelineNote = useCallback(
    async (item: CareTimelineItem) => {
      const appointment = appointments.find(({ appointmentId }) => appointmentId === item.appointmentId)
      const episode = getEpisode(item.episodeId)
      if (!appointment || !clientId) {
        showActionError()
        return
      }

      selectItemEpisode(episode)
      await completeNote(appointment, clientId)
    },
    [appointments, clientId, completeNote, getEpisode, selectItemEpisode, showActionError],
  )

  const watchRecording = useCallback(
    async (item: CareTimelineItem) => {
      if (!item.recordingId) {
        return
      }

      try {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: 'WATCH_RECORDING',
        })
        const recording = await getVideoSessionRecording({ id: item.recordingId }).unwrap()
        if (!recording.url) {
          showActionError()
          return
        }

        setRecordingModalContent(
          <VideoPlayer
            url={recording.url}
            reference={videoRef}
            onError={(error: Error) =>
              logToSentry('careTimelineRecording', {
                message: 'Unable to play Care Timeline session recording',
                error,
                recordingId: item.recordingId,
              })
            }
            height='100%'
            width='100%'
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              paddingTop: '40px',
            }}
            controls
            config={{
              file: {
                hlsOptions: {
                  xhrSetup: (xhr: XMLHttpRequest) => {
                    xhr.withCredentials = true
                    xhr.setRequestHeader('Access-Control-Allow-Headers', 'Content-Type, Accept, X-Requested-With')
                    xhr.setRequestHeader('Access-Control-Allow-Origin', cloudfrontOriginUrl)
                    xhr.setRequestHeader('Access-Control-Allow-Credentials', 'true')
                  },
                },
              },
            }}
            playing
          />,
        )
      } catch {
        showActionError()
      }
    },
    [getVideoSessionRecording, showActionError],
  )

  const closeRecordingModal = useCallback(() => setRecordingModalContent(null), [])

  const intakeAssignmentByEpisodeId = useMemo(() => {
    const map = new Map<string, Assignment>()
    episodes.forEach((episode) => {
      const episodeProgramConfig = programConfig[episode.program_name]
      const intakeAssignment = assignments.find(
        (assignment) =>
          [
            episodeProgramConfig?.clientIntake,
            episodeProgramConfig?.oldClientIntake,
            episodeProgramConfig?.clientIntakeNonUS,
          ].includes(assignment.content.name) &&
          assignment.episode_id === episode.id &&
          assignment.response_count > 0,
      )
      if (intakeAssignment) {
        map.set(episode.id, intakeAssignment)
      }
    })
    return map
  }, [assignments, episodes])

  const [intakeModalContent, setIntakeModalContent] = useState<JSX.Element | null>(null)
  const closeIntakeModal = useCallback(() => setIntakeModalContent(null), [])

  const openIntakeForm = useCallback(
    (item: CareTimelineItem) => {
      const intakeAssignment = intakeAssignmentByEpisodeId.get(item.episodeId)
      if (!intakeAssignment) {
        showActionError()
        return
      }

      const intakeResponse = intakeAssignment.assignment_responses?.[0]
      const response = intakeResponse?.response
      const date = intakeResponse?.submit_date
      const dateDisplay = date
        ? `${intl.formatMessage({ defaultMessage: 'Submitted', description: 'Label preceding a submission date' })} ${getFriendlyDate({ date, intl, addPreposition: false, displayTime: false })}`
        : undefined
      const isLCxClientIntake = clientIntakesLCx.includes(intakeAssignment.content?.name ?? '')
      const cancellationPolicyText =
        isLCxClientIntake && response?.cancellationConsentText ? (
          <ClientIntakeCancellationPolicyText
            policyText={response.cancellationConsentText}
            navigateToCancellationPolicyFAQ={() => {}}
          />
        ) : null

      setIntakeModalContent(
        <>
          <FormContentHeader
            title={intakeAssignment.content?.title}
            group={intakeAssignment.content?.group}
            dateDisplay={dateDisplay}
          />
          <FormBody
            schema={intakeAssignment.content_meta_data?.schema}
            initialValues={response}
            uiSchema={intakeAssignment.content_meta_data?.uiSchema}
            instructions={intakeAssignment.instructions}
            name={intakeAssignment.content?.name}
            saveForm={() => {}}
            readOnly
            intl={intl}
            formButton={undefined}
            withPageBreaks={false}
            useCustomizedActivityIntroduction={intakeAssignment.content_meta_data?.useCustomizedActivityIntroduction}
            hasNewLookAndFeel
            formContext={{
              externalValues: {
                shouldShowCancellationPolicyCheckbox: Boolean(cancellationPolicyText),
              },
              customCheckboxTitle: cancellationPolicyText,
            }}
          />
        </>,
      )
    },
    [intakeAssignmentByEpisodeId, intl, showActionError],
  )

  const [dischargeNoteModalContent, setDischargeNoteModalContent] = useState<JSX.Element | null>(null)
  const closeDischargeNoteModal = useCallback(() => setDischargeNoteModalContent(null), [])

  const openDischargeNote = useCallback(
    (item: CareTimelineItem, formattedDate: string) => {
      const terminationStatuses = isDAEngageProvider ? episodeTerminationStatusesEngageDA : episodeTerminationStatuses
      const firstName = clientDetails?.first_name

      setDischargeNoteModalContent(
        <View testID={tID('CareTimeline-discharge-note')}>
          <DischargeHeader>
            <Title size={TitleSize.MEDIUM} color={colors.textPrimary} style={{ fontWeight: 700 }}>
              {intl.formatMessage({
                defaultMessage: 'Discharge details',
                description: 'Title of the Care Timeline discharge details modal',
              })}
            </Title>
            <BodyText text={formattedDate} size={BodyTextSize.DEFAULT} color={colors.textPrimary} />
          </DischargeHeader>
          <DischargeFormContainer>
            <RadioGroup
              label={
                firstName
                  ? intl.formatMessage(
                      {
                        defaultMessage: 'What is the reason for discharging {firstName}?',
                        description: 'Radio question asking why the provider needs to terminate the episode',
                      },
                      { firstName },
                    )
                  : intl.formatMessage({
                      defaultMessage: 'What is the reason?',
                      description: 'Radio question asking why the provider needs to terminate the episode',
                    })
              }
              buttons={terminationStatuses.map(({ text, value }) => ({ label: text, value }))}
              buttonType={RadioButtonType.CIRCLE_TEXT_OUTLINE}
              selectedValue={item.dischargeReason}
              onChange={noop}
              readOnly
            />
            <InputField
              label={intl.formatMessage({
                defaultMessage: 'Comment',
                description: 'Text area for comments on why provider is closing session',
              })}
              value={item.dischargeComment ?? ''}
              numberOfLines={3}
              multiline
              readOnly
              testID={tID('CareTimeline-discharge-comment')}
            />
          </DischargeFormContainer>
        </View>,
      )
    },
    [clientDetails?.first_name, colors.textPrimary, intl, isDAEngageProvider],
  )

  return {
    addAsyncTimeNote,
    addCareReferral,
    addGeneralNote,
    addSessionNote,
    canAddAsyncTimeNote,
    closeCareTimelineSession,
    closeDischargeNoteModal,
    closeIntakeModal,
    completeCareTimelineNote,
    closeRecordingModal,
    dischargeNoteModalContent,
    intakeAssignmentByEpisodeId,
    intakeModalContent,
    isDAEngageProvider,
    isLoadingVideoSessions,
    navigateToExistingNote,
    openDischargeNote,
    openIntakeForm,
    recordingIdByAppointmentId,
    recordingModalContent,
    watchRecording,
  }
}
